import { findLast } from 'lodash';

import type {
    AiMessage,
    AiMessagesListResponse,
    AiPostMessageResponse,
    SparksProactiveQuestion
} from '@/components/composite/SparksAgent/types';

import { AI_CONVERSATION_MESSAGES_PAGE_SIZE, OPTIMISTIC_ID_PREFIX } from '../const';

import type { SparksMessage } from '@/components/composite/SparksAgent/SparksConversation';

import { QUESTION_POOL } from '../sparksQuestions';

class SparksAgentService {
    // ------------------------
    // Public helpers

    /**
     * AC02 + AC03: Selects 3 questions per session with no duplicates,
     * always including at least 1 from core_period_comparison.
     */
    getQuickQuestions = (): SparksProactiveQuestion[] => {
        const core = QUESTION_POOL.filter((q) => q.intent === 'core_period_comparison');
        const rest = QUESTION_POOL.filter((q) => q.intent !== 'core_period_comparison');

        const selectedCore = core[Math.floor(Math.random() * core.length)];
        const shuffledRest = [...rest].sort(() => Math.random() - 0.5).slice(0, 2);

        return [selectedCore, ...shuffledRest].sort(() => Math.random() - 0.5);
    };

    // ------------------------

    isFenceCodeClass(className?: string): boolean {
        return !!className && /(^|\s)language-[\w-]+/.test(className);
    }

    getAiMessageText(m: AiMessage): string {
        return m.role === 'assistant' ? m.output ?? m.content ?? '' : m.input ?? m.content ?? '';
    }

    toSparksMessage(m: AiMessage): SparksMessage {
        return {
            id: m.id,
            role: m.role === 'assistant' ? 'agent' : 'user',
            content: this.getAiMessageText(m),
            tool_results: m.tool_results,
            created_at: m.created_at
        };
    }

    parseSendMessageResponse(rawData: AiPostMessageResponse): AiMessage {
        const input = this.toNullableString(rawData.input);
        const output = this.toNullableString(rawData.output);

        return {
            // POST /messages does not currently return a persisted message id, so
            // we generate a stable client-side key until the next revalidation.
            id: `ai-res-${Date.now()}`,
            role: 'assistant',
            input: input ?? undefined,
            output: output ?? undefined,
            context: this.asArray(rawData.context),
            tool_results: this.asArray(rawData.tool_results),
            created_at: new Date().toISOString()
        };
    }

    findNewestPendingOptimisticUserMessage(messages: AiMessage[]): AiMessage | undefined {
        return findLast(
            messages,
            (m) => m.role === 'user' && m.id.startsWith(OPTIMISTIC_ID_PREFIX)
        );
    }

    createOptimisticUserMessage(text: string): AiMessage {
        return {
            id: `${OPTIMISTIC_ID_PREFIX}${Date.now()}`,
            role: 'user',
            input: text,
            created_at: new Date().toISOString()
        };
    }

    appendOptimisticToInfiniteData(args: {
        pages: AiMessagesListResponse[] | undefined;
        message: AiMessage;
        perPage: number;
    }): AiMessagesListResponse[] {
        const { pages, message, perPage } = args;

        if (!pages?.length) {
            return [this.createPage([message], perPage)];
        }

        const [first, ...rest] = pages;
        return [this.prependToPage(first, message), ...rest];
    }

    appendAIOutputToInfiniteData(args: {
        pages: AiMessagesListResponse[] | undefined;
        serverMessage: AiMessage;
    }): AiMessagesListResponse[] {
        const { pages, serverMessage } = args;

        if (!pages?.length) {
            return [this.createPage([serverMessage], AI_CONVERSATION_MESSAGES_PAGE_SIZE)];
        }

        const [first, ...rest] = pages;

        return [
            {
                ...first,
                data: [serverMessage, ...first.data]
            },
            ...rest
        ];
    }

    // ------------------------
    // SWR page data helpers
    // ------------------------

    private createPage(data: AiMessage[], perPage: number): AiMessagesListResponse {
        return {
            ...this.emptyMessagesPage(perPage),
            data
        };
    }

    private prependToPage(
        page: AiMessagesListResponse,
        message: AiMessage
    ): AiMessagesListResponse {
        return {
            ...page,
            data: [message, ...(page.data ?? [])]
        };
    }

    private emptyMessagesPage(perPage: number): AiMessagesListResponse {
        return {
            data: [],
            next_cursor: null,
            prev_cursor: null,
            next_page_url: null,
            prev_page_url: null,
            path: '',
            per_page: perPage
        };
    }

    // ------------------------
    // Generic utils
    // ------------------------

    private toNullableString(value: unknown): string | null {
        return value != null ? String(value) : null;
    }

    private asArray<T>(value: unknown): T[] | undefined {
        return Array.isArray(value) ? (value as T[]) : undefined;
    }
}

const sparksAgentService = new SparksAgentService();

export { sparksAgentService as SparksAgentService };
